// جميع الاستيرادات في أعلى الملف
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaRedo,
  FaPrint,
  FaFileExcel,
} from "react-icons/fa";
import { FaToggleOn, FaToggleOff } from "react-icons/fa";
import {
  getContracts,
  deleteContracts,
} from "../../../../redux/Slices/ContractSlice";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
  chunkArray,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import {
  getContractsCache,
  saveContractsCache,
  generateContractsCacheKey,
  getAllContractsCacheForQuery,
  clearOldContractsCache,
  isOnline,
  removeContractFromCache,
  getDropdownCache,
  saveDropdownCache,
  generateDropdownCacheKey,
  getAnyDropdownCache,
} from "../../../../shared/utils.js/offlineQueue";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";

const CustomStatusSelect = ({
  value,
  onChange,
  options,
  t,
  i18n,
  disabled,
  isRTL,
}) => {
  const [open, setOpen] = useState(false);
  // Prevent body scroll when dropdown is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);
  const ref = useRef();
  const [dropdownPos, setDropdownPos] = useState(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const dropdownWidth = Math.max(rect.width, 120);
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX + rect.width / 2,
        width: dropdownWidth,
      });
    }
  }, [open, isRTL]);

  const selected = options.find(opt => opt.value === value);

  return (
    <>
      <div
        ref={ref}
        style={{
          position: "relative",
          minWidth: 80,
          maxWidth: 120,
          userSelect: "none",
          fontWeight: "bold",
          fontSize: 14,
          textAlign: "center",
          direction: isRTL ? "rtl" : "ltr",
          zIndex: 100,
          display: "inline-block",
          verticalAlign: "middle",
        }}
      >
        <button
          type="button"
          onClick={() => !disabled && setOpen(o => !o)}
          disabled={disabled}
          style={{
            width: "100%",
            background: "#23263a",
            color: "#fff",
            border: "1px solid #444",
            borderRadius: 6,
            padding: "8px 10px",
            cursor: disabled ? "not-allowed" : "pointer",
            fontWeight: "bold",
            fontSize: 14,
            textAlign: "center",
            outline: "none",
            minWidth: 80,
            maxWidth: 120,
            transition: "background 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              flex: 1,
              textAlign: "center",
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selected
              ? selected.value === "inactive"
                ? i18n.language === "en"
                  ? "Inactive"
                  : "غير نشط"
                : t(selected.labelKey, { defaultValue: selected.value })
              : t("contracts.actions.changeStatus", {
                  defaultValue: "تغيير الحالة",
                })}
          </span>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              marginInlineStart: 4,
              pointerEvents: "none",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.5 6L8 9.5L11.5 6"
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>
      {open &&
        !disabled &&
        dropdownPos &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              transform: "translateX(-50%)",
              background: "#23263a",
              border: "1px solid #444",
              borderRadius: 8,
              zIndex: 9999,
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              padding: 0,
              marginTop: 0,
            }}
          >
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {options.map(opt => (
                <div
                  key={opt.value}
                  onMouseDown={e => {
                    e.preventDefault();
                    setOpen(false);
                    if (opt.value !== value) onChange(opt.value);
                  }}
                  style={{
                    padding: "10px 16px",
                    color: opt.value === value ? "#AC2000" : "#fff",
                    background: opt.value === value ? "#1a1f2e" : "transparent",
                    cursor: opt.value === value ? "default" : "pointer",
                    fontWeight: opt.value === value ? "bold" : "normal",
                    borderBottom: "1px solid #2a2d3a",
                    textAlign: "center",
                    userSelect: "none",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={e => {
                    if (opt.value !== value)
                      e.currentTarget.style.background = "#2a2d3a";
                  }}
                  onMouseLeave={e => {
                    if (opt.value !== value)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt.value === "inactive"
                    ? i18n.language === "en"
                      ? "Inactive"
                      : "غير نشط"
                    : t(opt.labelKey, { defaultValue: opt.value })}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const Contracts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  // تغيير حالة العقد من الدروب داون
  const CONTRACT_STATUSES = [
    { value: "draft", labelKey: "contracts.view.statusValues.draft" },
    { value: "active", labelKey: "contracts.view.statusValues.active" },
    { value: "completed", labelKey: "contracts.view.statusValues.completed" },
    { value: "cancelled", labelKey: "contracts.view.statusValues.cancelled" },
    {
      value: "inactive",
      labelKey: "contracts.view.statusValues.inactive",
      defaultValue: undefined,
    },
  ];

  const handleChangeStatus = async (contract, newStatus) => {
    if (contract.status === newStatus) return;
    try {
      const token = getToken();
      await axios.put(
        `${BASE_URL}/contracts/${contract.id}`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        t("contracts.actions.statusChanged", {
          defaultValue: "تم تغيير حالة العقد",
        }),
        { rtl: isRTL },
      );
      setContracts(prev =>
        prev.map(c => (c.id === contract.id ? { ...c, status: newStatus } : c)),
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          t("contracts.actions.toggleError", {
            defaultValue: "حدث خطأ أثناء تغيير الحالة",
          }),
        { rtl: isRTL },
      );
    }
  };
  const [contracts, setContracts] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState("10");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [projectId, setProjectId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [contractForPrint, setContractForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [fullContractsMap, setFullContractsMap] = useState({}); // Map of contractId -> full contract data
  const [contractItemsChunks, setContractItemsChunks] = useState({}); // Map of contractId -> items chunks
  const publicUrl = (
    process.env.PUBLIC_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/$/, "");
  const headerLogo = `${publicUrl}/ACE.png`;
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPagination, setCustomerPagination] = useState(null);
  const projectSearchInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const offlineToastShownRef = useRef(false);
  const dropdownOfflineToastShownRef = useRef({
    projects: false,
    customers: false,
  });

  const paginationInfo = useMemo(() => {
    if (!pagination) return { current_page: currentPage, last_page: 1 };
    return {
      current_page: pagination.current_page || pagination.page || currentPage,
      last_page: pagination.last_page || pagination.total_pages || 1,
      total: pagination.total || 0,
    };
  }, [pagination, currentPage]);

  const handleApplyPerPage = () => {
    const total = pagination?.total || contracts.length || 0;
    const maxValue = total > 0 ? total : 1000;
    const numeric = parseInt(perPageInput, 10) || 1;
    const safeValue = Math.max(1, Math.min(maxValue, numeric));
    setPerPage(safeValue);
    setPerPageInput(String(safeValue));
    setCurrentPage(1);
  };

  const fetchContracts = useCallback(
    async (
      page = currentPage,
      search = searchTerm,
      project = projectId,
      customer = customerId,
      status = statusFilter,
      perPageValue = perPage,
      sort = sortBy,
      order = sortOrder,
    ) => {
      try {
        const queryParams = {
          page,
          per_page: perPageValue,
          search: search || undefined,
          project_id: project || undefined,
          customer_id: customer || undefined,
          status: status || undefined,
          sort_by: sort || undefined,
          sort_order: order || undefined,
        };
        const cacheKey = generateContractsCacheKey(queryParams);

        // Step 1: try to load from IndexedDB first
        let cachedData = null;
        try {
          cachedData = await getContractsCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.contracts)) {
            setContracts(cachedData.contracts);
            setPagination(cachedData.pagination);
          }
        } catch (cacheError) {
          console.error("Contracts: error loading from cache:", cacheError);
        }

        // Step 2: sync with API when online
        if (isOnline()) {
          try {
            setIsLoading(true);
            const payload = await dispatch(getContracts(queryParams)).unwrap();
            const { items, pagination: paginationMeta } =
              normalizeListResponse(payload);

            // Update cache
            try {
              await saveContractsCache(cacheKey, items, paginationMeta);
            } catch (saveError) {
              console.error("Contracts: error saving to cache:", saveError);
            }

            // Update local UI
            setContracts(items);
            setPagination(paginationMeta);
          } catch (apiError) {
            console.error("Contracts: API fetch error:", apiError);
            // Try to fall back to all cached contracts for pagination
            const allCachedResult = await getAllContractsCacheForQuery({
              per_page: perPageValue,
              search,
              project_id: project,
              customer_id: customer,
              status,
            });
            const allCached = allCachedResult.items;

            if (Array.isArray(allCached) && allCached.length > 0) {
              const total = allCached.length;
              const lastPage = Math.max(1, Math.ceil(total / perPageValue));
              const safePage = Math.min(page, lastPage);
              const startIndex = (safePage - 1) * perPageValue;
              const pageItems = allCached.slice(
                startIndex,
                startIndex + perPageValue,
              );

              setContracts(pageItems);
              setPagination({
                current_page: safePage,
                last_page: lastPage,
                per_page: perPageValue,
                total,
              });

              if (!offlineToastShownRef.current) {
                offlineToastShownRef.current = true;
                toast.warning(
                  isRTL
                    ? "لا يمكن الاتصال بالخادم. يتم عرض العقود المحفوظة محلياً."
                    : "Cannot connect to server. Showing locally saved contracts.",
                  { rtl: isRTL, autoClose: 3000 },
                );
              }
            } else {
              const msg =
                typeof apiError === "string"
                  ? apiError
                  : t("contracts.messages.fetchError");
              toast.error(msg, { rtl: isRTL });
              setContracts([]);
              setPagination(null);
            }
          } finally {
            setIsLoading(false);
          }
        } else {
          // Offline: rely on cache if available
          const allCachedResult = await getAllContractsCacheForQuery({
            per_page: perPageValue,
            search,
            project_id: project,
            customer_id: customer,
            status,
          });
          const allCached = allCachedResult.items;

          if (Array.isArray(allCached) && allCached.length > 0) {
            const total = allCached.length;
            const lastPage = Math.max(1, Math.ceil(total / perPageValue));
            const safePage = Math.min(page, lastPage);
            const startIndex = (safePage - 1) * perPageValue;
            const pageItems = allCached.slice(
              startIndex,
              startIndex + perPageValue,
            );

            setContracts(pageItems);
            setPagination({
              current_page: safePage,
              last_page: lastPage,
              per_page: perPageValue,
              total,
            });

            if (!offlineToastShownRef.current) {
              offlineToastShownRef.current = true;
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض العقود المحفوظة محلياً."
                  : "No internet connection. Showing locally saved contracts.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
          } else {
            if (!offlineToastShownRef.current) {
              offlineToastShownRef.current = true;
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات عقود محفوظة."
                  : "No internet connection and no locally saved contracts.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
            setContracts([]);
            setPagination(null);
          }
        }
      } catch (error) {
        console.error("Contracts: error in fetchContracts:", error);
        toast.error(t("contracts.messages.fetchError"), { rtl: isRTL });
      }
    },
    [dispatch, t, isRTL, perPage],
  );

  const handleSort = column => {
    if (sortBy === column) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      setCurrentPage(1);
      fetchContracts(1, searchTerm, projectId, customerId, statusFilter, perPage, column, newOrder);
    } else {
      setSortBy(column);
      setSortOrder("asc");
      setCurrentPage(1);
      fetchContracts(1, searchTerm, projectId, customerId, statusFilter, perPage, column, "asc");
    }
  };

  const getSortIcon = column => {
    if (sortBy !== column) return " ↕";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  useEffect(() => {
    setTitle("sidenav.contracts");
  }, [setTitle]);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      const isClickInsideDropdown = event.target.closest(
        "[data-dropdown-container]",
      );
      const isClickOnDropdownToggle = event.target.closest(
        "[data-dropdown-toggle]",
      );

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setProjectDropdownOpen(false);
        setCustomerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close other dropdowns when opening a new one
  useEffect(() => {
    if (projectDropdownOpen) {
      setCustomerDropdownOpen(false);
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (customerDropdownOpen) {
      setProjectDropdownOpen(false);
    }
  }, [customerDropdownOpen]);

  useEffect(() => {
    fetchContracts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchContracts(
      currentPage,
      searchTerm,
      projectId,
      customerId,
      statusFilter,
      perPage,
      sortBy,
      sortOrder,
    );
  }, [
    currentPage,
    searchTerm,
    projectId,
    customerId,
    statusFilter,
    perPage,
    sortBy,
    sortOrder,
    fetchContracts,
  ]);

  // Clear old cache once
  useEffect(() => {
    clearOldContractsCache();
  }, []);

  // Auto-refresh when coming back online
  useEffect(() => {
    const handleOnline = () => {
      fetchContracts(
        currentPage,
        searchTerm,
        projectId,
        customerId,
        statusFilter,
        perPage,
      );
    };

    const handleOfflineRequestsRetried = event => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        const hasSuccessfulRetries = detail.results.some(
          r => r.success === true,
        );
        if (hasSuccessfulRetries) {
          const hasContractAction = detail.results.some(
            r => r.success === true && r.request?.url?.includes("/contracts"),
          );
          if (hasContractAction) {
            fetchContracts(
              currentPage,
              searchTerm,
              projectId,
              customerId,
              statusFilter,
              perPage,
            );
            toast.success(
              isRTL
                ? "تمت مزامنة التعديلات على العقود بنجاح بعد عودة الاتصال."
                : "Contract changes synced successfully after reconnecting.",
              { rtl: isRTL, autoClose: 3000 },
            );
          }
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [
    currentPage,
    searchTerm,
    projectId,
    customerId,
    statusFilter,
    perPage,
    fetchContracts,
    isRTL,
  ]);

  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };
        const cacheKey = generateDropdownCacheKey("projects", queryParams);

        // Try cache first
        const cached = await getDropdownCache(cacheKey);
        if (cached) {
          setProjectOptions(cached.items || []);
          setProjectPagination(cached.pagination || null);
        }

        if (isOnline()) {
          const token = getToken();
          if (!token) throw new Error(t("contracts.messages.noAuthToken"));
          const currentLang = i18n.language || "ar";
          const response = await axios.get(`${BASE_URL}/projects`, {
            params: queryParams,
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": currentLang,
            },
          });
          const { items, pagination: paginationMeta } = normalizeListResponse(
            response.data,
          );

          await saveDropdownCache(cacheKey, items, paginationMeta);
          setProjectOptions(items);
          setProjectPagination(paginationMeta);
        } else if (!cached) {
          // If offline and no specific cache, try any available projects
          const anyCached = await getAnyDropdownCache("projects");
          if (anyCached.length > 0) {
            setProjectOptions(anyCached);
            if (!dropdownOfflineToastShownRef.current.projects) {
              dropdownOfflineToastShownRef.current.projects = true;
              toast.info(
                isRTL
                  ? "يتم عرض المشاريع المحفوظة محلياً"
                  : "Showing locally saved projects",
                { autoClose: 2000 },
              );
            }
          }
        }
      } catch (error) {
        console.error("Fetch projects error:", error);
      }
    },
    [t, i18n, isRTL],
  );

  const fetchCustomers = useCallback(
    async (page = customerPage, search = customerSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };
        const cacheKey = generateDropdownCacheKey("customers", queryParams);

        // Try cache first
        const cached = await getDropdownCache(cacheKey);
        if (cached) {
          setCustomerOptions(cached.items || []);
          setCustomerPagination(cached.pagination || null);
        }

        if (isOnline()) {
          const token = getToken();
          if (!token) throw new Error(t("contracts.messages.noAuthToken"));
          const currentLang = i18n.language || "ar";
          const response = await axios.get(`${BASE_URL}/customers`, {
            params: queryParams,
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": currentLang,
            },
          });
          const { items, pagination: paginationMeta } = normalizeListResponse(
            response.data,
          );

          await saveDropdownCache(cacheKey, items, paginationMeta);
          setCustomerOptions(items);
          setCustomerPagination(paginationMeta);
        } else if (!cached) {
          // If offline and no specific cache, try any available customers
          const anyCached = await getAnyDropdownCache("customers");
          if (anyCached.length > 0) {
            setCustomerOptions(anyCached);
            if (!dropdownOfflineToastShownRef.current.customers) {
              dropdownOfflineToastShownRef.current.customers = true;
              toast.info(
                isRTL
                  ? "يتم عرض العملاء المحفوظين محلياً"
                  : "Showing locally saved customers",
                { autoClose: 2000 },
              );
            }
          }
        }
      } catch (error) {
        console.error("Fetch customers error:", error);
      }
    },
    [t, i18n, isRTL],
  );

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
  }, [projectDropdownOpen, projectPage, projectSearchTerm, fetchProjects]);

  useEffect(() => {
    if (customerDropdownOpen) {
      fetchCustomers(customerPage, customerSearchTerm);
    }
  }, [customerDropdownOpen, customerPage, customerSearchTerm, fetchCustomers]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [projectSearchTerm, projectDropdownOpen, fetchProjects]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setCustomerPage(1);
        fetchCustomers(1, customerSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [customerSearchTerm, customerDropdownOpen, fetchCustomers]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm("");
    setProjectId("");
    setCustomerId("");
    setStatusFilter("");
    setSortBy("");
    setSortOrder("desc");
    setCurrentPage(1);
    setProjectSearchTerm("");
    setProjectPage(1);
    setCustomerSearchTerm("");
    setCustomerPage(1);
  };

  const handleDelete = async () => {
    if (!contractToDelete) return;
    const idToDelete = contractToDelete.id;
    const contractToDeleteBackup = contractToDelete;
    setDeleteModalOpen(false);
    setContractToDelete(null);

    // Optimistically remove from local state first
    const updatedContracts = contracts.filter(
      contract => contract.id !== idToDelete,
    );
    setContracts(updatedContracts);

    try {
      const queryParams = {
        page: currentPage,
        per_page: perPage,
        search: searchTerm || undefined,
        project_id: projectId || undefined,
        customer_id: customerId || undefined,
        status: statusFilter || undefined,
      };
      const cacheKey = generateContractsCacheKey(queryParams);

      try {
        // Try to use offline request manager first
        await makeOfflineRequest(`/contracts/${idToDelete}`, "DELETE", null);
        toast.success(t("contracts.messages.deleteSuccess"), { rtl: isRTL });

        // Update pagination meta
        if (pagination && pagination.total > 0) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - 1),
          });
        }

        // Update cache
        await saveContractsCache(
          cacheKey,
          updatedContracts,
          pagination
            ? { ...pagination, total: Math.max(0, pagination.total - 1) }
            : null,
        );
      } catch (offlineError) {
        if (offlineError.message === "OFFLINE_REQUEST_SAVED") {
          // Remove from all cache entries optimistically
          await removeContractFromCache(idToDelete);

          if (pagination && pagination.total > 0) {
            setPagination({
              ...pagination,
              total: Math.max(0, pagination.total - 1),
            });
          }

          toast.info(
            isRTL
              ? "سيتم حذف العقد عند عودة الاتصال بالإنترنت"
              : "Contract will be deleted when connection is restored",
            { rtl: isRTL, autoClose: 5000 },
          );
          return;
        }

        // Fallback to Redux for other errors
        await dispatch(deleteContracts(idToDelete)).unwrap();
        toast.success(t("contracts.messages.deleteSuccess"), { rtl: isRTL });

        if (pagination && pagination.total > 0) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - 1),
          });
        }
        await saveContractsCache(
          cacheKey,
          updatedContracts,
          pagination
            ? { ...pagination, total: Math.max(0, pagination.total - 1) }
            : null,
        );
      }
    } catch (error) {
      // Revert optimistic update on error
      setContracts(prev => {
        const exists = prev.find(contract => contract.id === idToDelete);
        if (!exists) {
          return [...prev, contractToDeleteBackup].sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        }
        return prev;
      });
      toast.error(error || t("contracts.messages.deleteError"), { rtl: isRTL });
    }
  };

  const formatCurrency = amount => {
    if (!amount) return "0.00";
    const locale = "en-US";
    const formatted = parseFloat(amount).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return convertArabicNumbersToEnglish(formatted);
  };

  const convertNumberToArabicWords = num => {
    const numStr = parseFloat(num).toFixed(2);
    const [integerPart] = numStr.split(".");
    let intNum = parseInt(integerPart, 10);

    const ones = [
      "",
      "واحد",
      "اثنان",
      "ثلاثة",
      "أربعة",
      "خمسة",
      "ستة",
      "سبعة",
      "ثمانية",
      "تسعة",
    ];
    const tens = [
      "",
      "",
      "عشرون",
      "ثلاثون",
      "أربعون",
      "خمسون",
      "ستون",
      "سبعون",
      "ثمانون",
      "تسعون",
    ];
    const teens = [
      "عشرة",
      "أحد عشر",
      "اثنا عشر",
      "ثلاثة عشر",
      "أربعة عشر",
      "خمسة عشر",
      "ستة عشر",
      "سبعة عشر",
      "ثمانية عشر",
      "تسعة عشر",
    ];
    const hundreds = [
      "",
      "مائة",
      "مائتان",
      "ثلاثمائة",
      "أربعمائة",
      "خمسمائة",
      "ستمائة",
      "سبعمائة",
      "ثمانمائة",
      "تسعمائة",
    ];

    const convertThreeDigits = n => {
      if (n === 0) return "";
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) {
        const ten = Math.floor(n / 10);
        const one = n % 10;
        if (one === 0) return tens[ten];
        return `${ones[one]} و${tens[ten]}`;
      }
      const hundred = Math.floor(n / 100);
      const remainder = n % 100;
      if (remainder === 0) return hundreds[hundred];
      return `${hundreds[hundred]} و${convertThreeDigits(remainder)}`;
    };

    if (intNum === 0) return "صفر";

    let result = "";
    if (intNum >= 1000000) {
      const millions = Math.floor(intNum / 1000000);
      result += convertThreeDigits(millions) + " مليون";
      const remainder = intNum % 1000000;
      if (remainder > 0) result += " و";
      intNum = remainder;
    }
    if (intNum >= 1000) {
      const thousands = Math.floor(intNum / 1000);
      if (thousands === 1) {
        result += " ألف";
      } else if (thousands === 2) {
        result += " ألفان";
      } else if (thousands >= 3 && thousands <= 10) {
        result += convertThreeDigits(thousands) + " آلاف";
      } else {
        result += convertThreeDigits(thousands) + " ألف";
      }
      const remainder = intNum % 1000;
      if (remainder > 0) result += " و";
      intNum = remainder;
    }
    if (intNum > 0) {
      result += convertThreeDigits(intNum);
    }
    return result.trim() + " جنيه مصري";
  };

  const getStatusText = status => {
    const statusMap = {
      draft: t("contracts.view.statusValues.draft"),
      active: t("contracts.view.statusValues.active"),
      completed: t("contracts.view.statusValues.completed"),
      cancelled: t("contracts.view.statusValues.cancelled"),
    };
    return statusMap[status] || status || "—";
  };

  // Handle print single contract
  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  const fetchContractDetails = async contractId => {
    try {
      const token = getToken();
      if (!token) throw new Error(t("contracts.messages.noAuthToken"));
      const currentLang = i18n.language || "ar";
      const response = await axios.get(`${BASE_URL}/contracts/${contractId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data?.data || response.data || null;
    } catch (error) {
      console.error("Error fetching contract details:", error);
      return null;
    }
  };

  const handleViewContract = async contract => {
    try {
      setIsLoading(true);
      // Try to fetch full contract details
      const fullContract = await fetchContractDetails(contract.id);
      if (fullContract) {
        setContractForPrint(fullContract);
      } else {
        // Fallback to contract from list
        setContractForPrint(contract);
      }
      setPdfType("single");
      setIsPreviewOpen(true);
    } catch (error) {
      console.error("Error loading contract details:", error);
      // Fallback to contract from list
      setContractForPrint(contract);
      setPdfType("single");
      setIsPreviewOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintContract = async contract => {
    try {
      setIsGeneratingPdf(true);

      // Fetch full contract details if not already loaded
      let contractToPrint = contract;
      if (
        !contract.items ||
        !Array.isArray(contract.items) ||
        contract.items.length === 0
      ) {
        const fullContract = await fetchContractDetails(contract.id);
        if (fullContract) {
          contractToPrint = fullContract;
          // Update the full contracts map
          setFullContractsMap(prev => ({
            ...prev,
            [contract.id]: fullContract,
          }));
          // Wait a bit for React to update the DOM
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } else if (fullContractsMap[contract.id]) {
        contractToPrint = fullContractsMap[contract.id];
      }

      // Split items into chunks: 2 items in first page, 14 items in second page (with signatures), 15 items per page after that
      const items =
        contractToPrint.items && Array.isArray(contractToPrint.items)
          ? contractToPrint.items
          : [];
      let itemsChunks = [];
      if (items.length > 0) {
        // First chunk: 2 items (or all items if less than 2)
        const firstChunk = items.slice(0, 2);
        itemsChunks.push(firstChunk);
        // Second chunk: 14 items (will have signatures) - always create second page if there are any items
        if (items.length > 2) {
          const secondChunk = items.slice(2, 16); // 2 + 14 = 16
          itemsChunks.push(secondChunk);
          // Remaining chunks: 15 items per page
          if (items.length > 16) {
            const remainingItems = items.slice(16);
            const remainingChunks = chunkArray(remainingItems, 15);
            itemsChunks = [...itemsChunks, ...remainingChunks];
          }
        } else {
          // If items.length <= 2, create empty second page for signatures
          itemsChunks.push([]);
        }
      } else {
        // If no items, create one empty chunk
        itemsChunks = [[]];
      }
      // Store chunks for template rendering
      setContractItemsChunks(prev => ({
        ...prev,
        [contract.id]: itemsChunks,
      }));

      // Wait for React to update the DOM
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 300));

      // لا تفتح المودال ولا تغير حالة contractForPrint أو pdfType
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify all elements exist before generating PDF
      for (let i = 0; i < itemsChunks.length; i++) {
        const element = document.getElementById(
          `contract-pdf-${contract.id}-${i}`,
        );
        if (!element && i === 0) {
          toast.error(
            t("contracts.print.error", {
              defaultValue: "خطأ في تحميل قالب الطباعة",
            }),
            { rtl: isRTL },
          );
          setIsGeneratingPdf(false);
          setContractItemsChunks(prev => {
            const newState = { ...prev };
            delete newState[contract.id];
            return newState;
          });
          return;
        }
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < itemsChunks.length; chunkIndex++) {
        await new Promise(resolve => requestAnimationFrame(resolve));

        const element = document.getElementById(
          `contract-pdf-${contract.id}-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(
              t("contracts.print.error", {
                defaultValue: "خطأ في تحميل قالب الطباعة",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setContractItemsChunks(prev => {
              const newState = { ...prev };
              delete newState[contract.id];
              return newState;
            });
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale: scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: true,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `contract-pdf-${contract.id}-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = "rtl";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";

              const allElements = clonedElement.querySelectorAll("*");
              allElements.forEach(el => {
                if (el.style) {
                  el.style.fontFamily =
                    '"Times New Roman Bold", "Times New Roman", "Times", serif';
                  el.style.fontWeight = "bold";
                  el.style.setProperty("-webkit-font-smoothing", "antialiased");
                  el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                  el.style.textRendering = "optimizeLegibility";
                }
              });
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        // Tolerance: if the difference is less than 2mm, treat it as one page
        const tolerance = 2; // 2mm tolerance
        const effectiveHeight =
          imgHeight <= pdfHeight + tolerance ? pdfHeight : imgHeight;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        if (imgHeight <= pdfHeight + tolerance) {
          // If height is within tolerance, fit to one page
          pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            pdfWidth,
            Math.min(imgHeight, pdfHeight),
            undefined,
            "FAST",
          );
        } else {
          let position = 0;
          while (position < imgHeight) {
            if (position > 0) {
              pdf.addPage();
            }
            const remainingHeight = imgHeight - position;
            const pageHeight = Math.min(remainingHeight, pdfHeight);
            pdf.addImage(
              imgData,
              "PNG",
              0,
              0,
              pdfWidth,
              pageHeight,
              undefined,
              "FAST",
            );
            position += pdfHeight;
          }
        }
      }

      // Clean up chunks state
      setContractItemsChunks(prev => {
        const newState = { ...prev };
        delete newState[contract.id];
        return newState;
      });

      const identifier = contract.contract_no || contract.code || contract.id;
      pdf.save(
        `ACE_Contract_${identifier}_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("contracts.print.success", { defaultValue: "تم تنزيل PDF بنجاح" }),
        { rtl: isRTL },
      );
    } catch (err) {
      console.error("Contract PDF error:", err);
      toast.error(
        t("contracts.print.error", { defaultValue: "حدث خطأ أثناء إنشاء PDF" }),
        { rtl: isRTL },
      );
      // Clean up chunks state on error
      setContractItemsChunks(prev => {
        const newState = { ...prev };
        delete newState[contract.id];
        return newState;
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle print all contracts
  const handlePrintAll = async () => {
    if (!contracts || contracts.length === 0) {
      toast.error(
        t("contracts.print.noData", { defaultValue: "لا توجد بيانات للطباعة" }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setIsGeneratingPdf(true);
      setPdfType("all");
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = document.getElementById("contracts-pdf-all");
      if (!element) {
        toast.error(
          t("contracts.print.error", {
            defaultValue: "قالب الطباعة غير متوفر",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById("contracts-pdf-all");
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";
            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Tolerance: if the difference is less than 2mm, treat it as one page
      const tolerance = 2; // 2mm tolerance

      if (imgHeight <= pdfHeight + tolerance) {
        // If height is within tolerance, fit to one page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          Math.min(imgHeight, pdfHeight),
          undefined,
          "FAST",
        );
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            pdfWidth,
            pageHeight,
            undefined,
            "FAST",
          );
          position += pdfHeight;
        }
      }

      pdf.save(`ACE_Contracts_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Contracts PDF error:", error);
      toast.error(
        t("contracts.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء ملف العقود",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
    }
  };

  // Handle export single contract to Excel
  const handleExportContractToExcel = contract => {
    const projectName = isRTL
      ? contract.project?.name_ar || contract.project?.name_en
      : contract.project?.name_en || contract.project?.name_ar;
    const customerName = isRTL
      ? contract.customer?.name_ar || contract.customer?.name_en
      : contract.customer?.name_en || contract.customer?.name_ar;

    const data = [
      {
        [t("contracts.tableHeaders.contractNumber", {
          defaultValue: "رقم العقد",
        })]: contract.contract_no || contract.code || contract.id || "—",
        [t("contracts.tableHeaders.project", { defaultValue: "المشروع" })]:
          projectName || "—",
        [t("contracts.tableHeaders.customer", { defaultValue: "العميل" })]:
          customerName || "—",
        [t("contracts.tableHeaders.status", { defaultValue: "الحالة" })]:
          getStatusText(contract.status),
        [t("contracts.tableHeaders.startDate", {
          defaultValue: "تاريخ البدء",
        })]: formatDate(contract.start_date, isRTL) || "—",
        [t("contracts.tableHeaders.endDate", {
          defaultValue: "تاريخ الانتهاء",
        })]: formatDate(contract.end_date, isRTL) || "—",
        [t("contracts.tableHeaders.totalAmount", {
          defaultValue: "المبلغ الإجمالي",
        })]: formatCurrency(contract.total_amount),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? "عقد" : "Contract");
    const identifier = contract.contract_no || contract.code || contract.id;
    const fileName = `${isRTL ? "عقد" : "Contract"}_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("contracts.export.success", {
        defaultValue: "تم تصدير البيانات بنجاح",
      }),
      { rtl: isRTL },
    );
  };

  // Handle export all contracts to Excel
  const handleExportAllToExcel = () => {
    if (!contracts || contracts.length === 0) {
      toast.error(
        t("contracts.export.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    const data = contracts.map((contract, index) => {
      const projectName = isRTL
        ? contract.project?.name_ar || contract.project?.name_en
        : contract.project?.name_en || contract.project?.name_ar;
      const customerName = isRTL
        ? contract.customer?.name_ar || contract.customer?.name_en
        : contract.customer?.name_en || contract.customer?.name_ar;

      return {
        م: (paginationInfo.current_page - 1) * perPage + (index + 1),
        [t("contracts.tableHeaders.contractNumber", {
          defaultValue: "رقم العقد",
        })]: contract.contract_no || contract.code || contract.id || "—",
        [t("contracts.tableHeaders.project", { defaultValue: "المشروع" })]:
          projectName || "—",
        [t("contracts.tableHeaders.customer", { defaultValue: "العميل" })]:
          customerName || "—",
        [t("contracts.tableHeaders.status", { defaultValue: "الحالة" })]:
          getStatusText(contract.status),
        [t("contracts.tableHeaders.startDate", {
          defaultValue: "تاريخ البدء",
        })]: formatDate(contract.start_date, isRTL) || "—",
        [t("contracts.tableHeaders.endDate", {
          defaultValue: "تاريخ الانتهاء",
        })]: formatDate(contract.end_date, isRTL) || "—",
        [t("contracts.tableHeaders.totalAmount", {
          defaultValue: "المبلغ الإجمالي",
        })]: formatCurrency(contract.total_amount),
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? "العقود" : "Contracts");
    const fileName = `${isRTL ? "العقود" : "Contracts"}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("contracts.export.success", {
        defaultValue: "تم تصدير البيانات بنجاح",
      }),
      { rtl: isRTL },
    );
  };

  return (
    <div
      style={{
        padding: 30,
        backgroundColor: "#14263b",
        minHeight: "100vh",
        color: "white",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20 }}>
          {t("contracts.pageTitle", {
            defaultValue: "Contracts",
          })}
        </h1>
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "Per Page" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => {
                  const current = parseInt(perPageInput, 10) || 1;
                  const newValue = Math.max(1, current - 1);
                  setPerPageInput(String(newValue));
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                max={pagination?.total || contracts.length || 1000}
                value={perPageInput}
                onChange={e => {
                  setPerPageInput(e.target.value);
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const total = pagination?.total || contracts.length || 0;
                  const maxValue = total > 0 ? total : 1000;
                  const current = parseInt(perPageInput, 10) || 1;
                  const newValue = Math.min(maxValue, current + 1);
                  setPerPageInput(String(newValue));
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12 }}
      >
        <div style={{ position: "relative", flex: "1 1 320px" }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#888",
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t("contracts.searchPlaceholder")}
            style={{
              width: "100%",
              padding: isRTL ? "12px 42px 12px 16px" : "12px 16px 12px 42px",
              backgroundColor: "#1a1f2e",
              border: "1px solid #333",
              borderRadius: 8,
              color: "white",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          />
        </div>
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setProjectDropdownOpen(prev => !prev)}
            style={{
              ...dropdownDisplayStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <span>
              {projectId
                ? (() => {
                    const project = projectOptions.find(
                      p => String(p.id) === String(projectId),
                    );
                    if (project) {
                      return isRTL
                        ? project.name_ar || project.name_en
                        : project.name_en || project.name_ar;
                    }
                    return `${t("contracts.project")} #${projectId}`;
                  })()
                : t("contracts.selectProject")}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={projectDropdownOpen}
            searchTerm={projectSearchTerm}
            onSearchChange={setProjectSearchTerm}
            searchInputRef={projectSearchInputRef}
            items={projectOptions}
            pagination={projectPagination}
            onPageChange={setProjectPage}
            onClose={() => setProjectDropdownOpen(false)}
            onSelect={item => {
              setProjectId(String(item.id));
              setProjectDropdownOpen(false);
              setProjectSearchTerm("");
              setProjectPage(1);
              setCurrentPage(1);
            }}
            label={t("contracts.project")}
            isRTL={isRTL}
            i18n={i18n}
            zIndex={9999}
          />
        </div>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}
      >
        <div
          style={{ position: "relative", flex: "1 1 320px" }}
          data-dropdown-container
        >
          <div
            data-dropdown-toggle
            onClick={() => setCustomerDropdownOpen(prev => !prev)}
            style={{
              ...dropdownDisplayStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <span>
              {customerId
                ? (() => {
                    const customer = customerOptions.find(
                      c => String(c.id) === String(customerId),
                    );
                    if (customer) {
                      return isRTL
                        ? customer.name_ar || customer.name_en
                        : customer.name_en || customer.name_ar;
                    }
                    return `${t("contracts.customer")} #${customerId}`;
                  })()
                : t("contracts.selectCustomer")}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={customerDropdownOpen}
            searchTerm={customerSearchTerm}
            onSearchChange={setCustomerSearchTerm}
            searchInputRef={customerSearchInputRef}
            items={customerOptions}
            pagination={customerPagination}
            onPageChange={setCustomerPage}
            onClose={() => setCustomerDropdownOpen(false)}
            onSelect={item => {
              setCustomerId(String(item.id));
              setCustomerDropdownOpen(false);
              setCustomerSearchTerm("");
              setCustomerPage(1);
              setCurrentPage(1);
            }}
            label={t("contracts.customer")}
            isRTL={isRTL}
            i18n={i18n}
          />
        </div>
        <div style={{ flex: "1 1 320px" }}>
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              ...filterInputStyle,
              width: "100%",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <option value="">{t("contracts.all")}</option>
            <option value="draft">
              {t("contracts.view.statusValues.draft")}
            </option>
            <option value="active">
              {t("contracts.view.statusValues.active")}
            </option>
            <option value="completed">
              {t("contracts.view.statusValues.completed")}
            </option>
            <option value="cancelled">
              {t("contracts.view.statusValues.cancelled")}
            </option>
          </select>
        </div>
      </div>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleReset}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            width: "auto",
          }}
        >
          {t("btns.reset")}
        </button>
        <button
          onClick={() => navigate("/contracts/add")}
          style={{
            backgroundColor: "#AC2000",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaPlus /> {t("contracts.newContract")}
        </button>
        <button
          onClick={() => {
            setPdfType("all");
            setContractForPrint(null);
            setIsPreviewOpen(true);
          }}
          style={{
            backgroundColor: "#444",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaEye />{" "}
          {t("contracts.preview.all", { defaultValue: "عرض PDF للكل" })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{
            backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            opacity: isGeneratingPdf ? 0.6 : 1,
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaPrint />{" "}
          {isGeneratingPdf
            ? t("contracts.print.generating", {
                defaultValue: "جاري الطباعة...",
              })
            : t("contracts.print.all", { defaultValue: "طباعة الكل" })}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: 8,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "auto",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaFileExcel />{" "}
          {t("contracts.export.all", { defaultValue: "تصدير Excel الكل" })}
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 48,
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 1000,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: 14,
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: 14,
                    borderBottom: "1px solid #333",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: 60,
                  }}
                  onClick={() => handleSort("id")}
                >
                  #{getSortIcon("id")}
                </th>
                {[
                  { key: "contract_no", label: t("contracts.tableHeaders.contractNumber") },
                  { key: "project_id", label: t("contracts.tableHeaders.project") },
                  { key: "customer_id", label: t("contracts.tableHeaders.customer") },
                  { key: "status", label: t("contracts.tableHeaders.status") },
                  { key: "start_date", label: t("contracts.tableHeaders.startDate") },
                  { key: "end_date", label: t("contracts.tableHeaders.endDate") },
                  { key: null, label: t("contracts.tableHeaders.actions") },
                ].map(col => (
                  <th
                    key={col.label}
                    style={{
                      padding: 14,
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: 14,
                      borderBottom: "1px solid #333",
                      cursor: col.key ? "pointer" : "default",
                      whiteSpace: "nowrap",
                    }}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}{col.key ? getSortIcon(col.key) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("contracts.loading")}
                  </td>
                </tr>
              ) : contracts.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ textAlign: "center", padding: 20, color: "#ccc" }}
                  >
                    {t("contracts.noData")}
                  </td>
                </tr>
              ) : (
                contracts.map((contract, index) => (
                  <tr
                    key={contract.id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td style={{ ...cellStyle, width: 60 }}>
                      {(paginationInfo.current_page - 1) * perPage + (index + 1)}
                    </td>
                    <td style={cellStyle}>
                      {contract.contract_no ||
                        contract.code ||
                        contract.id ||
                        "—"}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 180,
                      }}
                    >
                      {isRTL
                        ? contract.project?.name_ar ||
                          contract.project?.name_en ||
                          "—"
                        : contract.project?.name_en ||
                          contract.project?.name_ar ||
                          "—"}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 180,
                      }}
                    >
                      {isRTL
                        ? contract.customer?.name_ar ||
                          contract.customer?.name_en ||
                          "—"
                        : contract.customer?.name_en ||
                          contract.customer?.name_ar ||
                          "—"}
                    </td>
                    <td style={cellStyle}>
                      {renderStatus(contract.status, t, i18n)}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 140, maxWidth: 180 }}>
                      {formatDate(contract.start_date, isRTL)}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 140, maxWidth: 180 }}>
                      {formatDate(contract.end_date, isRTL)}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 340, maxWidth: 400 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 16,
                          flexWrap: "nowrap",
                        }}
                      >
                        <CustomStatusSelect
                          value={contract.status}
                          onChange={newStatus =>
                            handleChangeStatus(contract, newStatus)
                          }
                          options={CONTRACT_STATUSES}
                          t={t}
                          i18n={i18n}
                          disabled={isGeneratingPdf}
                          isRTL={isRTL}
                        />
                        <ActionButton
                          title={t("btns.view")}
                          onClick={() => handleViewContract(contract)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t("contracts.print.single", {
                            defaultValue: "طباعة",
                          })}
                          onClick={() => handlePrintContract(contract)}
                          disabled={isGeneratingPdf}
                          bg={isGeneratingPdf ? "#666" : "#17a2b8"}
                          style={{
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                          }}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t("contracts.export.single", {
                            defaultValue: "تصدير Excel",
                          })}
                          bg="#28a745"
                          onClick={() => handleExportContractToExcel(contract)}
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.edit")}
                          onClick={() =>
                            navigate(`/contracts/edit/${contract.id}`)
                          }
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.delete")}
                          bg="#c0392b"
                          onClick={() => {
                            setContractToDelete(contract);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            padding: 16,
            borderTop: "1px solid #333",
          }}
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={paginationInfo.current_page === 1}
            style={paginationButton(paginationInfo.current_page === 1)}
          >
            {t("btns.previous")}
          </button>
          <span style={{ padding: 8 }}>
            {paginationInfo.current_page} / {paginationInfo.last_page}
          </span>
          <button
            onClick={() =>
              setCurrentPage(prev =>
                prev >= paginationInfo.last_page
                  ? paginationInfo.last_page
                  : prev + 1,
              )
            }
            disabled={paginationInfo.current_page >= paginationInfo.last_page}
            style={paginationButton(
              paginationInfo.current_page >= paginationInfo.last_page,
            )}
          >
            {t("btns.next")}
          </button>
        </div>
      </div>
      {deleteModalOpen && (
        <div style={modalOverlayStyle}>
          <div
            style={{
              ...modalCardStyle,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>
              {t("contracts.deleteModal.title")}
            </h3>
            <p style={{ marginBottom: 24 }}>
              {t("contracts.deleteModal.message")}{" "}
              <strong>
                {contractToDelete?.contract_no ||
                  contractToDelete?.code ||
                  contractToDelete?.id}
              </strong>
              ؟
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: isRTL ? "flex-end" : "flex-start",
                gap: 12,
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setContractToDelete(null);
                }}
              >
                {t("contracts.deleteModal.cancel")}
              </button>
              <button style={modalPrimaryButtonStyle} onClick={handleDelete}>
                {t("contracts.deleteModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Always render hidden PDF templates for all contracts */}
      {contracts.map(contract => {
        // Use full contract data if available, otherwise use contract from list
        const contractData = fullContractsMap[contract.id] || contract;
        const items =
          contractData.items && Array.isArray(contractData.items)
            ? contractData.items
            : [];
        // Split items into chunks: 2 items in first page, 14 items in second page (with signatures), 15 items per page after that
        let itemsChunks = [];
        if (items.length > 0) {
          // First chunk: 2 items (or all items if less than 2)
          const firstChunk = items.slice(0, 2);
          itemsChunks.push(firstChunk);
          // Second chunk: 14 items (will have signatures) - always create second page if there are any items
          if (items.length > 2) {
            const secondChunk = items.slice(2, 16); // 2 + 14 = 16
            itemsChunks.push(secondChunk);
            // Remaining chunks: 15 items per page
            if (items.length > 16) {
              const remainingItems = items.slice(16);
              const remainingChunks = chunkArray(remainingItems, 15);
              itemsChunks = [...itemsChunks, ...remainingChunks];
            }
          } else {
            itemsChunks.push([]);
          }
        } else {
          // If no items, create one empty chunk
          itemsChunks = [[]];
        }

        // Render a template for each chunk
        return itemsChunks.map((chunk, chunkIndex) => {
          const isFirstChunk = chunkIndex === 0;
          const showContractDetails = isFirstChunk; // Show contract details only on first page
          const showItems = true; // Show items on all pages
          return (
            <div
              key={`${contract.id}-${chunkIndex}`}
              id={`contract-pdf-${contract.id}-${chunkIndex}`}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                top: 0,
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "1043px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    height: "1033px",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    direction: "rtl",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.08,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    {/* Contract Title */}
                    {showContractDetails &&
                      (() => {
                        const projectName = isRTL
                          ? contractData.project?.name_ar ||
                            contractData.project?.name_en
                          : contractData.project?.name_en ||
                            contractData.project?.name_ar;
                        return (
                          <div
                            style={{
                              textAlign: "center",
                              marginBottom: "16px",
                              padding: "8px 12px",
                              direction: "rtl",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "20px",
                                color: "#000000",
                                fontWeight: "bold",
                              }}
                            >
                              {isRTL ? "عقد مشروع" : "Project Contract"}
                              {projectName ? ` ${projectName}` : ""}
                            </span>
                          </div>
                        );
                      })()}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "visible",
                        minHeight: 0,
                        paddingBottom: "180px",
                      }}
                    >
                      {/* Contract Preamble and Parties - Only show on first page */}
                      {showContractDetails &&
                        (() => {
                          const customerName = isRTL
                            ? contractData.customer?.name_ar ||
                              contractData.customer?.name_en
                            : contractData.customer?.name_en ||
                              contractData.customer?.name_ar;
                          const contractDate = contractData.date
                            ? formatDate(contractData.date, isRTL)
                            : "";
                          return (
                            <div
                              style={{
                                marginTop: "8px",
                                marginBottom: "8px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                direction: "rtl",
                                fontSize: "14px",
                                lineHeight: "1.8",
                                color: "#000",
                              }}
                            >
                              <div
                                style={{
                                  marginBottom: "12px",
                                  textAlign: "right",
                                }}
                              >
                                {isRTL ? "إنه في تاريخ" : "On date"}{" "}
                                {contractDate || "………"}{" "}
                                {isRTL
                                  ? "تم الاتفاق بين كلٍ من:"
                                  : "the following parties agreed:"}
                              </div>
                              <div
                                style={{
                                  marginBottom: "16px",
                                  textAlign: "right",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <span>
                                    {isRTL
                                      ? "مكتب البرقاوي مهندسون استشاريون"
                                      : "Albaraqawi Consulting Engineers"}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#666",
                                    fontWeight: "normal",
                                  }}
                                >
                                  ({isRTL ? "الطرف الأول" : "First Party"})
                                </span>
                              </div>
                              <div
                                style={{
                                  marginBottom: "16px",
                                  textAlign: "right",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <strong>
                                    {isRTL ? "السيد / ة:" : "Mr. / Mrs.:"}
                                  </strong>
                                  <span>{customerName || "—"}</span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#666",
                                    fontWeight: "normal",
                                  }}
                                >
                                  ({isRTL ? "الطرف الثاني" : "Second Party"})
                                </span>
                              </div>
                              <div
                                style={{
                                  marginBottom: "8px",
                                  textAlign: "right",
                                  fontStyle: "italic",
                                }}
                              >
                                {isRTL
                                  ? 'ويُشار إليهما فيما بعد بـ "الطرفين".'
                                  : 'Hereinafter referred to as "the Parties".'}
                              </div>
                            </div>
                          );
                        })()}

                      {/* Contract Data Section - Only show on first page */}
                      {showContractDetails &&
                        (() => {
                          const projectName = isRTL
                            ? contractData.project?.name_ar ||
                              contractData.project?.name_en
                            : contractData.project?.name_en ||
                              contractData.project?.name_ar;
                          const customerName = isRTL
                            ? contractData.customer?.name_ar ||
                              contractData.customer?.name_en
                            : contractData.customer?.name_en ||
                              contractData.customer?.name_ar;

                          return (
                            <div
                              style={{
                                marginTop: "8px",
                                marginBottom: "8px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                direction: "rtl",
                                fontSize: "14px",
                                lineHeight: "1.8",
                                color: "#000",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  marginBottom: "8px",
                                  textAlign: "right",
                                  color: "#1976d2",
                                }}
                              >
                                {isRTL ? "بيانات العقد" : "Contract Data"}
                              </div>
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL ? "رقم العقد:" : "Contract Number:"}
                                </strong>{" "}
                                {contractData.contract_no ||
                                  contractData.code ||
                                  contractData.id ||
                                  "—"}
                              </div>
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL ? "اسم المشروع:" : "Project Name:"}
                                </strong>{" "}
                                {projectName || "—"}
                              </div>
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL ? "العميل:" : "Customer:"}
                                </strong>{" "}
                                {customerName || "—"}
                              </div>
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL ? "حالة العقد:" : "Contract Status:"}
                                </strong>{" "}
                                {getStatusText(contractData.status)}
                              </div>
                              {/* Bill Category (فئة المستخلص) if present */}
                              {contractData.bill_category && (
                                <div
                                  style={{
                                    marginBottom: "6px",
                                    textAlign: "right",
                                  }}
                                >
                                  <strong>
                                    {isRTL ? "فئة المستخلص:" : "Bill Category:"}
                                  </strong>{" "}
                                  {(() => {
                                    const cat = contractData.bill_category;
                                    if (cat === "initial")
                                      return isRTL ? "أولي" : "Initial";
                                    if (cat === "final")
                                      return isRTL ? "ختامي" : "Final";
                                    if (cat === "running")
                                      return isRTL ? "جاري" : "Running";
                                    return cat;
                                  })()}
                                </div>
                              )}
                              {(contractData.start_date ||
                                contractData.end_date) && (
                                <div
                                  style={{
                                    marginBottom: "6px",
                                    textAlign: "right",
                                  }}
                                >
                                  <strong>
                                    {isRTL
                                      ? "يبدأ العقد في"
                                      : "Contract starts on"}
                                  </strong>{" "}
                                  {contractData.start_date
                                    ? formatDate(contractData.start_date, isRTL)
                                    : "………"}{" "}
                                  {isRTL ? "وينتهي في" : "and ends on"}{" "}
                                  {contractData.end_date
                                    ? formatDate(contractData.end_date, isRTL)
                                    : "………"}
                                </div>
                              )}
                              {contractData.quotation?.quotation_no && (
                                <div
                                  style={{
                                    marginBottom: "6px",
                                    textAlign: "right",
                                  }}
                                >
                                  <strong>
                                    {isRTL
                                      ? "رقم عرض السعر:"
                                      : "Quotation Number:"}
                                  </strong>{" "}
                                  {contractData.quotation.quotation_no}
                                </div>
                              )}
                              {contractData.notes && (
                                <div
                                  style={{
                                    marginBottom: "6px",
                                    textAlign: "right",
                                  }}
                                >
                                  <strong>
                                    {isRTL ? "ملاحظات:" : "Notes:"}
                                  </strong>{" "}
                                  {contractData.notes}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {/* Contract Items Table - Show chunk items */}
                      {showItems && chunk.length > 0 && (
                        <div
                          style={{
                            marginTop: showContractDetails ? "8px" : "12px",
                          }}
                        >
                          {isFirstChunk && (
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                marginBottom: "8px",
                                color: "#1976d2",
                                direction: "rtl",
                                textAlign: "right",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                lineHeight: "1.8",
                              }}
                            >
                              {isRTL ? "بنود العقد" : "Contract Items"}
                            </div>
                          )}
                          {isFirstChunk && (
                            <div
                              style={{
                                fontSize: "14px",
                                marginBottom: "8px",
                                direction: "rtl",
                                textAlign: "right",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                lineHeight: "1.8",
                              }}
                            >
                              {isRTL
                                ? "اتفق الطرفان على تنفيذ الأعمال التالية وفقًا للبنود والأسعار الموضحة أدناه:"
                                : "The parties agreed to execute the following works according to the items and prices shown below:"}
                            </div>
                          )}
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              border: "2px solid #000",
                              fontSize: "12px",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: "rgba(227, 242, 253, 0.6)",
                                }}
                              >
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "#" : "#"}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "الوصف" : "Description"}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "الوحدة" : "Unit"}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "الكمية" : "Quantity"}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "سعر الوحدة" : "Unit Price"}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    direction: "rtl",
                                  }}
                                >
                                  {isRTL ? "الإجمالي" : "Total"}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {chunk.map((item, idx) => {
                                const description = isRTL
                                  ? item.description_ar ||
                                    item.description_en ||
                                    "—"
                                  : item.description_en ||
                                    item.description_ar ||
                                    "—";
                                // Calculate the global index across all chunks (2 items in first page, 14 in second page, 15 in following pages)
                                let globalIndex;
                                if (isFirstChunk) {
                                  globalIndex = idx + 1; // First page: 1, 2
                                } else if (chunkIndex === 1) {
                                  globalIndex = 2 + idx + 1; // Second page: 3, 4, ..., 16
                                } else {
                                  globalIndex =
                                    16 + (chunkIndex - 2) * 15 + idx + 1; // Following pages
                                }
                                return (
                                  <tr
                                    key={item.id || idx}
                                    style={{
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                    }}
                                  >
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        direction: "rtl",
                                      }}
                                    >
                                      {globalIndex}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "right",
                                        direction: "rtl",
                                      }}
                                    >
                                      {description}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        direction: "rtl",
                                      }}
                                    >
                                      {item.unit || "—"}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        direction: "rtl",
                                      }}
                                    >
                                      {item.quantity
                                        ? parseFloat(
                                            item.quantity,
                                          ).toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        direction: "rtl",
                                      }}
                                    >
                                      {item.unit_price
                                        ? formatCurrency(item.unit_price)
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        fontWeight: "700",
                                        color: "#0057c2",
                                        direction: "rtl",
                                      }}
                                    >
                                      {item.total_price
                                        ? formatCurrency(item.total_price)
                                        : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* Show total: on first page if <= 2 items, otherwise on second page */}
                              {((items.length <= 2 && isFirstChunk) ||
                                (items.length > 2 && chunkIndex === 1)) && (
                                <tr
                                  style={{
                                    backgroundColor: "rgba(227, 242, 253, 0.4)",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <td
                                    colSpan={5}
                                    style={{
                                      border: "1px solid #000",
                                      padding: "8px",
                                      textAlign: "right",
                                      direction: "rtl",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {isRTL
                                      ? "إجمالي قيمة العقد:"
                                      : "Total Contract Value:"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      color: "#0057c2",
                                      fontSize: "14px",
                                      direction: "rtl",
                                    }}
                                  >
                                    {formatCurrency(contractData.total_amount)}{" "}
                                    {isRTL ? "جنيه مصري" : "EGP"}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          {/* Total in words: on first page if <= 2 items, otherwise on second page */}
                          {((items.length <= 2 && isFirstChunk) ||
                            (items.length > 2 && chunkIndex === 1)) && (
                            <div
                              style={{
                                marginTop: "12px",
                                fontSize: "14px",
                                direction: "rtl",
                                textAlign: "right",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                lineHeight: "1.8",
                                color: "#000",
                              }}
                            >
                              {isRTL ? "فقط وقدره:" : "Only and amounting to:"}{" "}
                              {convertNumberToArabicWords(
                                contractData.total_amount,
                              )}{" "}
                              {isRTL ? "لا غير" : "only"}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Contract Conclusion and Signatures - Only show on second page (chunkIndex === 1) */}
                      {showItems &&
                        chunkIndex === 1 &&
                        (() => {
                          const customerName = isRTL
                            ? contractData.customer?.name_ar ||
                              contractData.customer?.name_en
                            : contractData.customer?.name_en ||
                              contractData.customer?.name_ar;
                          return (
                            <div
                              style={{
                                marginTop: "24px",
                                marginBottom: "16px",
                                fontFamily:
                                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: "bold",
                                direction: "rtl",
                                fontSize: "14px",
                                lineHeight: "1.8",
                                color: "#000",
                              }}
                            >
                              <div
                                style={{
                                  marginBottom: "24px",
                                  textAlign: "right",
                                }}
                              >
                                {isRTL
                                  ? "ويقر الطرفان بمراجعتهما لكافة بنود هذا العقد، وموافقتهما عليها، والتزامهما بتنفيذها."
                                  : "Both parties acknowledge that they have reviewed all terms of this contract, agree to them, and commit to their implementation."}
                              </div>

                              {/* Signatures Section */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: "40px",
                                  marginTop: "32px",
                                  marginBottom: "16px",
                                }}
                              >
                                <div style={{ flex: 1, textAlign: "right" }}>
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {isRTL ? "الطرف الأول:" : "First Party:"}
                                  </div>
                                  <div style={{ marginBottom: "4px" }}>
                                    {isRTL ? "الاسم:" : "Name:"}{" "}
                                    ____________________
                                  </div>
                                  <div>
                                    {isRTL ? "التوقيع:" : "Signature:"}{" "}
                                    __________________
                                  </div>
                                </div>
                                <div style={{ flex: 1, textAlign: "right" }}>
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {isRTL ? "الطرف الثاني:" : "Second Party:"}
                                  </div>
                                  <div style={{ marginBottom: "4px" }}>
                                    {isRTL ? "الاسم:" : "Name:"}{" "}
                                    {customerName || "____________________"}
                                  </div>
                                  <div>
                                    {isRTL ? "التوقيع:" : "Signature:"}{" "}
                                    __________________
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "18px",
                        right: "18px",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                            borderTop: "4px double #000",
                            paddingTop: "8px",
                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        });
      })}

      {/* Print Template for All Contracts */}
      {pdfType === "all" && (
        <AllContractsPrintTemplate
          contracts={contracts}
          paginationInfo={paginationInfo}
          isRTL={isRTL}
          t={t}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusText={getStatusText}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          searchTerm={searchTerm}
          projectId={projectId}
          customerId={customerId}
          statusFilter={statusFilter}
          projectOptions={projectOptions}
          customerOptions={customerOptions}
          perPage={perPage}
        />
      )}

      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          (pdfType === "all"
            ? contracts && contracts.length > 0
            : !!contractForPrint)
        }
        title={
          pdfType === "all"
            ? t("contracts.print.all", { defaultValue: "طباعة الكل" })
            : t("contracts.preview.single", { defaultValue: "معاينة العقد" })
        }
        onClose={() => setIsPreviewOpen(false)}
        onPrint={
          pdfType === "all"
            ? handlePrintAll
            : () => contractForPrint && handlePrintContract(contractForPrint)
        }
        isGeneratingPdf={isGeneratingPdf}
      >
        {pdfType === "all" ? (
          <AllContractsPreviewTemplate
            contracts={contracts}
            paginationInfo={paginationInfo}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusText={getStatusText}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            searchTerm={searchTerm}
            projectId={projectId}
            customerId={customerId}
            statusFilter={statusFilter}
            projectOptions={projectOptions}
            customerOptions={customerOptions}
            perPage={perPage}
          />
        ) : contractForPrint ? (
          <SingleContractPreviewTemplate
            contract={contractForPrint}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusText={getStatusText}
            convertNumberToArabicWords={convertNumberToArabicWords}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
          />
        ) : null}
      </PdfPreviewModal>
    </div>
  );
};

const renderStatus = (status, t, i18n) => {
  switch (status) {
    case "active":
      return t("contracts.view.statusValues.active");
    case "inactive":
      // Show 'Inactive' in English, 'غير نشط' in Arabic
      return i18n?.language === "en" ? "Inactive" : "غير نشط";
    case "completed":
      return t("contracts.view.statusValues.completed");
    case "cancelled":
      return t("contracts.view.statusValues.cancelled");
    case "draft":
      return t("contracts.view.statusValues.draft");
    default:
      return status || "—";
  }
};

const formatDate = (value, isRTL) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US");
  } catch {
    return value;
  }
};

const filterInputStyle = {
  width: "100%",
  padding: 12,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
};

const cellStyle = {
  padding: 14,
  textAlign: "center",
  color: "white",
  fontSize: 14,
};

const paginationButton = disabled => ({
  padding: "8px 16px",
  backgroundColor: disabled ? "#333" : "#AC2000",
  color: "white",
  border: "none",
  borderRadius: 6,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const ActionButton = ({
  children,
  onClick,
  title,
  bg = "#2f3644",
  disabled = false,
  style = {},
}) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: 6,
      padding: "8px 10px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 38,
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
  >
    {children}
  </button>
);

const dropdownDisplayStyle = {
  width: "100%",
  padding: "12px 14px",
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  color: "white",
  textAlign: "right",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
};

const dropdownSearchStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: "#14263b",
  color: "white",
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: "center",
  color: "#ccc",
  fontSize: 13,
};

const dropdownItemStyle = active => ({
  padding: "10px 12px",
  borderRadius: 6,
  cursor: "pointer",
  color: "white",
  backgroundColor: active ? "#AC2000" : "transparent",
  marginBottom: 6,
  border: active ? "none" : "1px solid transparent",
});

const dropdownPaginationStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 10,
};

const dropdownPaginationButtonStyle = disabled => ({
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: disabled ? "#333" : "#AC2000",
  color: "white",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
  zIndex: dropdownZIndex,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : i18n?.language === "ar";
  const safeItems = Array.isArray(items)
    ? items
    : Array.isArray(items?.data)
      ? items.data
      : Array.isArray(items?.items)
        ? items.items
        : [];

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, safeItems, searchInputRef]);

  if (!isOpen) return null;

  const menuStyle = {
    ...dropdownMenuStyle,
    direction: dropdownIsRTL ? "rtl" : "ltr",
  };
  if (dropdownZIndex != null) menuStyle.zIndex = dropdownZIndex;

  return (
    <div style={menuStyle} onClick={e => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t("contracts.dropdown.searchPlaceholder")} ${label}`}
          value={searchTerm}
          onChange={e => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={{
            ...dropdownSearchStyle,
            direction: dropdownIsRTL ? "rtl" : "ltr",
            textAlign: dropdownIsRTL ? "right" : "left",
          }}
        />
      </div>
      {pagination &&
        (pagination.last_page > 1 ||
          (pagination.last_page === 1 && pagination.current_page === 1)) && (
          <div
            style={{
              padding: "8px",
              borderTop: "1px solid #333",
              background: "#14263b",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  color: "#ccc",
                  fontSize: 11,
                  direction: dropdownIsRTL ? "rtl" : "ltr",
                  textAlign: dropdownIsRTL ? "right" : "left",
                }}
              >
                {t("contracts.dropdown.page")} {pagination.current_page}{" "}
                {t("contracts.dropdown.of")} {pagination.last_page}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1) onPageChange(1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ««
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page > 1)
                    onPageChange(pagination.current_page - 1);
                }}
                disabled={pagination.current_page === 1}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                  color: pagination.current_page === 1 ? "#666" : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === 1 ? "not-allowed" : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ‹
              </button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1)
                  start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(page => (
                  <button
                    key={page}
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onPageChange(page);
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: page === current ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border:
                        page === current
                          ? "1px solid #AC2000"
                          : "1px solid #444",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: page === current ? "bold" : "normal",
                      minWidth: "28px",
                    }}
                  >
                    {page}
                  </button>
                ));
              })()}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.current_page + 1);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                ›
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (pagination.current_page < pagination.last_page)
                    onPageChange(pagination.last_page);
                }}
                disabled={pagination.current_page === pagination.last_page}
                style={{
                  padding: "4px 8px",
                  backgroundColor:
                    pagination.current_page === pagination.last_page
                      ? "#2a2a2a"
                      : "#1a1f2e",
                  color:
                    pagination.current_page === pagination.last_page
                      ? "#666"
                      : "white",
                  border: "1px solid #444",
                  borderRadius: "4px",
                  cursor:
                    pagination.current_page === pagination.last_page
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 10,
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                »»
              </button>
            </div>
          </div>
        )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {safeItems.length === 0 ? (
          <div
            style={{
              ...dropdownEmptyStyle,
              direction: dropdownIsRTL ? "rtl" : "ltr",
              textAlign: dropdownIsRTL ? "right" : "left",
            }}
          >
            {t("contracts.dropdown.noResults")}
          </div>
        ) : (
          safeItems.map(item => (
            <div
              key={item.id}
              style={{
                padding: "10px 12px",
                borderTop: "1px solid #333",
                cursor: "pointer",
                direction: dropdownIsRTL ? "rtl" : "ltr",
                textAlign: dropdownIsRTL ? "right" : "left",
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item, i18n?.language || "ar")}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity, language = "ar") => {
  if (!entity) return "—";
  const code = entity.code ? `${entity.code} - ` : "";
  const isRTL = language === "ar";
  const name = isRTL
    ? entity.name_ar || entity.name_en || ""
    : entity.name_en || entity.name_ar || "";
  return `${code}${name}` || `#${entity.id}`;
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: "#1b3350",
  borderRadius: 12,
  padding: 24,
  border: "1px solid #333",
  width: "100%",
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: "#AC2000",
  color: "white",
  border: "none",
  borderRadius: 8,
  padding: "10px 18px",
  cursor: "pointer",
};

const modalSecondaryButtonStyle = {
  backgroundColor: "transparent",
  color: "white",
  border: "1px solid #444",
  borderRadius: 8,
  padding: "10px 18px",
  cursor: "pointer",
};

// Print Template for All Contracts
const AllContractsPrintTemplate = ({
  contracts,
  paginationInfo,
  isRTL,
  t,
  formatDate,
  formatCurrency,
  getStatusText,
  publicUrl,
  headerLogo,
  searchTerm,
  projectId,
  customerId,
  statusFilter,
  projectOptions,
  customerOptions,
  perPage,
}) => {
  if (!contracts || contracts.length === 0) return null;

  return (
    <div
      id="contracts-pdf-all"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "794px",
        minHeight: "1123px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          minHeight: "1043px",
          height: "auto",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "auto",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
              flex: 1,
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  marginTop: "12px",
                  direction: isRTL ? "rtl" : "ltr",
                  fontFamily: '"Times New Roman", "Times", serif',
                }}
              >
                <h1
                  style={{
                    textAlign: "center",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    fontSize: "18px",
                    marginBottom: "4px",
                    marginTop: "8px",
                    color: "#000080",
                  }}
                >
                  {t("contracts.title", { defaultValue: "العقود" })}
                </h1>
                <p style={{ textAlign: "center", fontSize: "12px", margin: "0 0 6px 0", color: "#333" }}>
                  {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                </p>
                {(searchTerm || projectId || customerId || statusFilter) && (
                  <div style={{ textAlign: "center", fontSize: "11px", marginBottom: "8px", color: "#555", direction: isRTL ? "rtl" : "ltr" }}>
                    {[
                      searchTerm && `${t("contracts.searchPlaceholder", { defaultValue: "بحث" })}: ${searchTerm}`,
                      projectId && projectOptions.find(p => String(p.id) === String(projectId)) && `${t("contracts.project", { defaultValue: "المشروع" })}: ${isRTL ? (projectOptions.find(p => String(p.id) === String(projectId))?.name_ar || projectOptions.find(p => String(p.id) === String(projectId))?.name_en) : (projectOptions.find(p => String(p.id) === String(projectId))?.name_en || projectOptions.find(p => String(p.id) === String(projectId))?.name_ar)}`,
                      customerId && customerOptions.find(c => String(c.id) === String(customerId)) && `${t("contracts.customer", { defaultValue: "العميل" })}: ${isRTL ? (customerOptions.find(c => String(c.id) === String(customerId))?.name_ar || customerOptions.find(c => String(c.id) === String(customerId))?.name_en) : (customerOptions.find(c => String(c.id) === String(customerId))?.name_en || customerOptions.find(c => String(c.id) === String(customerId))?.name_ar)}`,
                      statusFilter && `${t("contracts.tableHeaders.status", { defaultValue: "الحالة" })}: ${getStatusText(statusFilter)}`,
                    ].filter(Boolean).join(" | ")}
                  </div>
                )}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                      <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "700", backgroundColor: "rgba(238, 242, 247, 0.7)", width: "36px" }}>#</th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.contractNumber", {
                          defaultValue: "رقم العقد",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.project", {
                          defaultValue: "المشروع",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.customer", {
                          defaultValue: "العميل",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.status", {
                          defaultValue: "الحالة",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.startDate", {
                          defaultValue: "تاريخ البدء",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.endDate", {
                          defaultValue: "تاريخ الانتهاء",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.totalAmount", {
                          defaultValue: "المبلغ الإجمالي",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract, idx) => {
                      const projectName = isRTL
                        ? contract.project?.name_ar || contract.project?.name_en
                        : contract.project?.name_en ||
                          contract.project?.name_ar;
                      const customerName = isRTL
                        ? contract.customer?.name_ar ||
                          contract.customer?.name_en
                        : contract.customer?.name_en ||
                          contract.customer?.name_ar;

                      return (
                        <tr
                          key={contract.id}
                          style={{
                            backgroundColor: idx % 2 === 0 ? "rgba(255, 255, 255, 0.7)" : "rgba(245, 248, 255, 0.7)",
                          }}
                        >
                          <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", width: "36px" }}>{(paginationInfo.current_page - 1) * perPage + idx + 1}</td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {contract.contract_no ||
                              contract.code ||
                              contract.id ||
                              "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {projectName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {customerName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {getStatusText(contract.status)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(contract.start_date, isRTL) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(contract.end_date, isRTL) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatCurrency(contract.total_amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              style={{
                marginTop: "auto",
                paddingTop: "20px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Template Components
const SingleContractPreviewTemplate = ({
  contract,
  isRTL,
  t,
  formatDate,
  formatCurrency,
  getStatusText,
  convertNumberToArabicWords,
  publicUrl,
  headerLogo,
}) => {
  if (!contract) return null;

  // Split items into chunks: 2 items in first page, 14 items in second page (with signatures), 15 items per page after that
  const items =
    contract.items && Array.isArray(contract.items) ? contract.items : [];
  let itemsChunks = [];
  if (items.length > 0) {
    // First chunk: 2 items (or all items if less than 2)
    const firstChunk = items.slice(0, 2);
    itemsChunks.push(firstChunk);
    // Second chunk: 14 items (will have signatures) - always create second page if there are any items
    if (items.length > 2) {
      const secondChunk = items.slice(2, 16); // 2 + 14 = 16
      itemsChunks.push(secondChunk);
      // Remaining chunks: 15 items per page
      if (items.length > 16) {
        const remainingItems = items.slice(16);
        const remainingChunks = chunkArray(remainingItems, 15);
        itemsChunks = [...itemsChunks, ...remainingChunks];
      }
    } else {
      // If items.length <= 2, create empty second page for signatures
      itemsChunks.push([]);
    }
  } else {
    // If no items, create one empty chunk
    itemsChunks = [[]];
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        alignItems: "center",
      }}
    >
      {itemsChunks.map((chunk, chunkIndex) => {
        const isFirstChunk = chunkIndex === 0;
        const showContractDetails = isFirstChunk; // Show contract details only on first page
        const showItems = true; // Show items on all pages

        return (
          <div
            key={chunkIndex}
            style={{
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              margin: "0 auto",
              overflow: "hidden",
              pageBreakAfter: "always",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: "rtl",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />
                  {/* Contract Title */}
                  {showContractDetails &&
                    (() => {
                      const projectName = isRTL
                        ? contract.project?.name_ar || contract.project?.name_en
                        : contract.project?.name_en ||
                          contract.project?.name_ar;
                      return (
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: "16px",
                            padding: "8px 12px",
                            direction: "rtl",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "20px",
                              color: "#000000",
                              fontWeight: "bold",
                            }}
                          >
                            {isRTL ? "عقد مشروع" : "Project Contract"}
                            {projectName ? ` ${projectName}` : ""}
                          </span>
                        </div>
                      );
                    })()}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                      paddingBottom: "180px",
                    }}
                  >
                    {/* Contract Preamble and Parties - Only show on first page */}
                    {showContractDetails &&
                      (() => {
                        const customerName = isRTL
                          ? contract.customer?.name_ar ||
                            contract.customer?.name_en
                          : contract.customer?.name_en ||
                            contract.customer?.name_ar;
                        const firstParty =
                          contract.first_party || contract.company_name || ""; // سيأتي من API
                        const contractDate = contract.date
                          ? formatDate(contract.date, isRTL)
                          : "";
                        return (
                          <div
                            style={{
                              marginTop: "8px",
                              marginBottom: "8px",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                              direction: "rtl",
                              fontSize: "14px",
                              lineHeight: "1.8",
                              color: "#000",
                            }}
                          >
                            <div
                              style={{
                                marginBottom: "12px",
                                textAlign: "right",
                              }}
                            >
                              {isRTL ? "إنه في تاريخ" : "On date"}{" "}
                              {contractDate || "………"}{" "}
                              {isRTL
                                ? "تم الاتفاق بين كلٍ من:"
                                : "the following parties agreed:"}
                            </div>
                            <div
                              style={{
                                marginBottom: "16px",
                                textAlign: "right",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <strong>
                                  {isRTL ? "السيد / ة:" : "Mr. / Mrs.:"}
                                </strong>
                                <span>
                                  {isRTL
                                    ? "مكتب البرقاوي مهندسون استشاريون"
                                    : "Albaraqawi Consulting Engineers"}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#666",
                                  fontWeight: "normal",
                                }}
                              >
                                ({isRTL ? "الطرف الأول" : "First Party"})
                              </span>
                            </div>
                            <div
                              style={{
                                marginBottom: "16px",
                                textAlign: "right",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <strong>
                                  {isRTL ? "السيد / ة:" : "Mr. / Mrs.:"}
                                </strong>
                                <span>{customerName || "—"}</span>
                              </div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#666",
                                  fontWeight: "normal",
                                }}
                              >
                                ({isRTL ? "الطرف الثاني" : "Second Party"})
                              </span>
                            </div>
                            <div
                              style={{
                                marginBottom: "8px",
                                textAlign: "right",
                                fontStyle: "italic",
                              }}
                            >
                              {isRTL
                                ? 'ويُشار إليهما فيما بعد بـ "الطرفين".'
                                : 'Hereinafter referred to as "the Parties".'}
                            </div>
                          </div>
                        );
                      })()}

                    {/* Contract Data Section - Only show on first page */}
                    {showContractDetails &&
                      (() => {
                        const projectName = isRTL
                          ? contract.project?.name_ar ||
                            contract.project?.name_en
                          : contract.project?.name_en ||
                            contract.project?.name_ar;
                        const customerName = isRTL
                          ? contract.customer?.name_ar ||
                            contract.customer?.name_en
                          : contract.customer?.name_en ||
                            contract.customer?.name_ar;

                        return (
                          <div
                            style={{
                              marginTop: "8px",
                              marginBottom: "8px",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                              direction: "rtl",
                              fontSize: "14px",
                              lineHeight: "1.8",
                              color: "#000",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                marginBottom: "8px",
                                textAlign: "right",
                                color: "#1976d2",
                              }}
                            >
                              {isRTL ? "بيانات العقد" : "Contract Data"}
                            </div>
                            <div
                              style={{
                                marginBottom: "6px",
                                textAlign: "right",
                              }}
                            >
                              <strong>
                                {isRTL ? "رقم العقد:" : "Contract Number:"}
                              </strong>{" "}
                              {contract.contract_no ||
                                contract.code ||
                                contract.id ||
                                "—"}
                            </div>
                            <div
                              style={{
                                marginBottom: "6px",
                                textAlign: "right",
                              }}
                            >
                              <strong>
                                {isRTL ? "اسم المشروع:" : "Project Name:"}
                              </strong>{" "}
                              {projectName || "—"}
                            </div>
                            <div
                              style={{
                                marginBottom: "6px",
                                textAlign: "right",
                              }}
                            >
                              <strong>{isRTL ? "العميل:" : "Customer:"}</strong>{" "}
                              {customerName || "—"}
                            </div>
                            <div
                              style={{
                                marginBottom: "6px",
                                textAlign: "right",
                              }}
                            >
                              <strong>
                                {isRTL ? "حالة العقد:" : "Contract Status:"}
                              </strong>{" "}
                              {getStatusText(contract.status)}
                            </div>
                            {(contract.start_date || contract.end_date) && (
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL
                                    ? "يبدأ العقد في"
                                    : "Contract starts on"}
                                </strong>{" "}
                                {contract.start_date
                                  ? formatDate(contract.start_date, isRTL)
                                  : "………"}{" "}
                                {isRTL ? "وينتهي في" : "and ends on"}{" "}
                                {contract.end_date
                                  ? formatDate(contract.end_date, isRTL)
                                  : "………"}
                              </div>
                            )}
                            {contract.quotation?.quotation_no && (
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>
                                  {isRTL
                                    ? "رقم عرض السعر:"
                                    : "Quotation Number:"}
                                </strong>{" "}
                                {contract.quotation.quotation_no}
                              </div>
                            )}
                            {contract.notes && (
                              <div
                                style={{
                                  marginBottom: "6px",
                                  textAlign: "right",
                                }}
                              >
                                <strong>{isRTL ? "ملاحظات:" : "Notes:"}</strong>{" "}
                                {contract.notes}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {/* Contract Items Table - Show chunk items */}
                    {showItems && chunk.length > 0 && (
                      <div
                        style={{
                          marginTop: showContractDetails ? "8px" : "12px",
                        }}
                      >
                        {isFirstChunk && (
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                              marginBottom: "8px",
                              color: "#1976d2",
                              direction: "rtl",
                              textAlign: "right",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              lineHeight: "1.8",
                            }}
                          >
                            {isRTL ? "بنود العقد" : "Contract Items"}
                          </div>
                        )}
                        {isFirstChunk && (
                          <div
                            style={{
                              fontSize: "14px",
                              marginBottom: "8px",
                              direction: "rtl",
                              textAlign: "right",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              lineHeight: "1.8",
                            }}
                          >
                            {isRTL
                              ? "اتفق الطرفان على تنفيذ الأعمال التالية وفقًا للبنود والأسعار الموضحة أدناه:"
                              : "The parties agreed to execute the following works according to the items and prices shown below:"}
                          </div>
                        )}
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "2px solid #000",
                            fontSize: "12px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "rgba(227, 242, 253, 0.6)",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "#" : "#"}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "الوصف" : "Description"}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "الوحدة" : "Unit"}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "الكمية" : "Quantity"}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "سعر الوحدة" : "Unit Price"}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "8px",
                                  textAlign: "center",
                                  fontWeight: "700",
                                  direction: "rtl",
                                }}
                              >
                                {isRTL ? "الإجمالي" : "Total"}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((item, idx) => {
                              const description = isRTL
                                ? item.description_ar ||
                                  item.description_en ||
                                  "—"
                                : item.description_en ||
                                  item.description_ar ||
                                  "—";
                              // Calculate the global index across all chunks (2 items in first page, 14 in second page, 15 in following pages)
                              let globalIndex;
                              if (isFirstChunk) {
                                globalIndex = idx + 1; // First page: 1, 2
                              } else if (chunkIndex === 1) {
                                globalIndex = 2 + idx + 1; // Second page: 3, 4, ..., 16
                              } else {
                                globalIndex =
                                  16 + (chunkIndex - 2) * 15 + idx + 1; // Following pages
                              }
                              return (
                                <tr
                                  key={item.id || idx}
                                  style={{
                                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                                  }}
                                >
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "center",
                                      direction: "rtl",
                                    }}
                                  >
                                    {globalIndex}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "right",
                                      direction: "rtl",
                                    }}
                                  >
                                    {description}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "center",
                                      direction: "rtl",
                                    }}
                                  >
                                    {item.unit || "—"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "center",
                                      direction: "rtl",
                                    }}
                                  >
                                    {item.quantity
                                      ? parseFloat(
                                          item.quantity,
                                        ).toLocaleString("en-US", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : "—"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "center",
                                      direction: "rtl",
                                    }}
                                  >
                                    {item.unit_price
                                      ? formatCurrency(item.unit_price)
                                      : "—"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #000",
                                      padding: "6px",
                                      textAlign: "center",
                                      fontWeight: "700",
                                      color: "#0057c2",
                                      direction: "rtl",
                                    }}
                                  >
                                    {item.total_price
                                      ? formatCurrency(item.total_price)
                                      : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Show total: on first page if <= 2 items, otherwise on second page */}
                            {((items.length <= 2 && isFirstChunk) ||
                              (items.length > 2 && chunkIndex === 1)) && (
                              <tr
                                style={{
                                  backgroundColor: "rgba(227, 242, 253, 0.4)",
                                  fontWeight: "bold",
                                }}
                              >
                                <td
                                  colSpan={5}
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "right",
                                    direction: "rtl",
                                    fontSize: "14px",
                                  }}
                                >
                                  {isRTL
                                    ? "إجمالي قيمة العقد:"
                                    : "Total Contract Value:"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    color: "#0057c2",
                                    fontSize: "14px",
                                    direction: "rtl",
                                  }}
                                >
                                  {formatCurrency(contract.total_amount)}{" "}
                                  {isRTL ? "جنيه مصري" : "EGP"}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {/* Total in words: on first page if <= 2 items, otherwise on second page */}
                        {((items.length <= 2 && isFirstChunk) ||
                          (items.length > 2 && chunkIndex === 1)) && (
                          <div
                            style={{
                              marginTop: "12px",
                              fontSize: "14px",
                              direction: "rtl",
                              textAlign: "right",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              lineHeight: "1.8",
                              color: "#000",
                            }}
                          >
                            {isRTL ? "فقط وقدره:" : "Only and amounting to:"}{" "}
                            {convertNumberToArabicWords(contract.total_amount)}{" "}
                            {isRTL ? "لا غير" : "only"}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Contract Conclusion and Signatures - Only show on second page (chunkIndex === 1) */}
                    {showItems &&
                      chunkIndex === 1 &&
                      (() => {
                        const customerName = isRTL
                          ? contract.customer?.name_ar ||
                            contract.customer?.name_en
                          : contract.customer?.name_en ||
                            contract.customer?.name_ar;
                        return (
                          <div
                            style={{
                              marginTop: "24px",
                              marginBottom: "16px",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                              direction: "rtl",
                              fontSize: "14px",
                              lineHeight: "1.8",
                              color: "#000",
                            }}
                          >
                            <div
                              style={{
                                marginBottom: "24px",
                                textAlign: "right",
                              }}
                            >
                              {isRTL
                                ? "ويقر الطرفان بمراجعتهما لكافة بنود هذا العقد، وموافقتهما عليها، والتزامهما بتنفيذها."
                                : "Both parties acknowledge that they have reviewed all terms of this contract, agree to them, and commit to their implementation."}
                            </div>

                            {/* Signatures Section */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "40px",
                                marginTop: "32px",
                                marginBottom: "16px",
                              }}
                            >
                              <div style={{ flex: 1, textAlign: "right" }}>
                                <div
                                  style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {isRTL ? "الطرف الأول:" : "First Party:"}
                                </div>
                                <div style={{ marginBottom: "4px" }}>
                                  {isRTL ? "الاسم:" : "Name:"}{" "}
                                  ____________________
                                </div>
                                <div>
                                  {isRTL ? "التوقيع:" : "Signature:"}{" "}
                                  __________________
                                </div>
                              </div>
                              <div style={{ flex: 1, textAlign: "right" }}>
                                <div
                                  style={{
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {isRTL ? "الطرف الثاني:" : "Second Party:"}
                                </div>
                                <div style={{ marginBottom: "4px" }}>
                                  {isRTL ? "الاسم:" : "Name:"}{" "}
                                  {customerName || "____________________"}
                                </div>
                                <div>
                                  {isRTL ? "التوقيع:" : "Signature:"}{" "}
                                  __________________
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AllContractsPreviewTemplate = ({
  contracts,
  paginationInfo,
  isRTL,
  t,
  formatDate,
  formatCurrency,
  getStatusText,
  publicUrl,
  headerLogo,
  searchTerm,
  projectId,
  customerId,
  statusFilter,
  projectOptions,
  customerOptions,
  perPage,
}) => {
  const i18n = useTranslation();
  if (!contracts || contracts.length === 0) return null;

  return (
    <div
      style={{
        width: "794px",
        minHeight: "1123px",
        height: "auto",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        margin: "0 auto",
        overflow: "visible",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          minHeight: "1043px",
          height: "auto",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "auto",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
              flex: 1,
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
              }}
            >
              <h1
                style={{
                  textAlign: "center",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  fontSize: "18px",
                  marginBottom: "4px",
                  marginTop: "8px",
                  color: "#000080",
                }}
              >
                {t("contracts.title", { defaultValue: "العقود" })}
              </h1>
              <p style={{ textAlign: "center", fontSize: "12px", margin: "0 0 6px 0", color: "#333" }}>
                {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
              </p>
              {(searchTerm || projectId || customerId || statusFilter) && (
                <div style={{ textAlign: "center", fontSize: "11px", marginBottom: "8px", color: "#555", direction: isRTL ? "rtl" : "ltr" }}>
                  {[
                    searchTerm && `${t("contracts.searchPlaceholder", { defaultValue: "بحث" })}: ${searchTerm}`,
                    projectId && projectOptions.find(p => String(p.id) === String(projectId)) && `${t("contracts.project", { defaultValue: "المشروع" })}: ${isRTL ? (projectOptions.find(p => String(p.id) === String(projectId))?.name_ar || projectOptions.find(p => String(p.id) === String(projectId))?.name_en) : (projectOptions.find(p => String(p.id) === String(projectId))?.name_en || projectOptions.find(p => String(p.id) === String(projectId))?.name_ar)}`,
                    customerId && customerOptions.find(c => String(c.id) === String(customerId)) && `${t("contracts.customer", { defaultValue: "العميل" })}: ${isRTL ? (customerOptions.find(c => String(c.id) === String(customerId))?.name_ar || customerOptions.find(c => String(c.id) === String(customerId))?.name_en) : (customerOptions.find(c => String(c.id) === String(customerId))?.name_en || customerOptions.find(c => String(c.id) === String(customerId))?.name_ar)}`,
                    statusFilter && `${t("contracts.tableHeaders.status", { defaultValue: "الحالة" })}: ${getStatusText(statusFilter)}`,
                  ].filter(Boolean).join(" | ")}
                </div>
              )}
              <div
                style={{
                  marginTop: "12px",
                  direction: isRTL ? "rtl" : "ltr",
                  fontFamily: '"Times New Roman", "Times", serif',
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    border: "1px solid #000",
                    fontFamily: '"Times New Roman", "Times", serif',
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}>
                      <th style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "700", backgroundColor: "rgba(238, 242, 247, 0.7)", width: "36px" }}>#</th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.contractNumber", {
                          defaultValue: "رقم العقد",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.project", {
                          defaultValue: "المشروع",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.customer", {
                          defaultValue: "العميل",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.status", {
                          defaultValue: "الحالة",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.startDate", {
                          defaultValue: "تاريخ البدء",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.endDate", {
                          defaultValue: "تاريخ الانتهاء",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          textAlign: "center",
                          fontWeight: "700",
                          backgroundColor: "rgba(238, 242, 247, 0.7)",
                        }}
                      >
                        {t("contracts.tableHeaders.totalAmount", {
                          defaultValue: "المبلغ الإجمالي",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract, idx) => {
                      const projectName = isRTL
                        ? contract.project?.name_ar || contract.project?.name_en
                        : contract.project?.name_en ||
                          contract.project?.name_ar;
                      const customerName = isRTL
                        ? contract.customer?.name_ar ||
                          contract.customer?.name_en
                        : contract.customer?.nameEn ||
                          contract.customer?.name_ar;

                      return (
                        <tr
                          key={contract.id}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", backgroundColor: "rgba(255, 255, 255, 0.7)", width: "36px" }}>{(paginationInfo.current_page - 1) * perPage + idx + 1}</td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {contract.contract_no ||
                              contract.code ||
                              contract.id ||
                              "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {projectName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              direction: isRTL ? "rtl" : "ltr",
                              textAlign: isRTL ? "right" : "left",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {customerName || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {getStatusText(contract.status)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(contract.start_date, isRTL) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatDate(contract.end_date, isRTL) || "—"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {formatCurrency(contract.total_amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              style={{
                marginTop: "auto",
                paddingTop: "20px",
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contracts;
